import { RefObject, useLayoutEffect, useRef } from "react"
import { timelineMaxZoom, timelineMinZoom } from "../../data/defaults/timeline.zoom.defaults"
import { anchoredScroll } from "../../helpers/wheelZoom.helpers"
import {
    setTimelinePreferencesAsync,
    setTimelinePreferencesFulfilled,
} from "../../store/slices/timelinePreferences.slice"
import { TIMELINE_ROW } from "../../style/sizing"
import { useAppDispatch, useAppSelector, useAppStore } from "../useRedux"
import { scrollTimelineTo } from "../useTimelineViewport"
import useWheelZoom from "../useWheelZoom"

/** A zoom waiting to be re-anchored once React has laid the new zoom out. */
type PendingAnchor = {
    /** the zoomed point's distance from 0 s, in px at `zoom` */
    timeOffset: number
    zoom: number
    /** the pointer's distance from the start of the visible time area */
    viewportOffset: number
}

/**
 * Cmd/Ctrl + wheel and pinch zoom for the timeline, horizontally and centred on the pointer:
 * the moment under the pointer stays under it — see useWheelZoom.
 *
 * The time area is linear in the zoom (see calcTimelineRowWidth) and starts after the
 * sticky row headings, so a moment `t` sits at `heading + t × zoom`. A pointer over the
 * headings anchors the first visible moment instead.
 *
 * @param scrollRef the horizontally scrollable timeline container
 */
const useTimelineWheelZoom = (scrollRef: RefObject<HTMLElement>) => {
    const dispatch = useAppDispatch()
    const store = useAppStore()
    const timelineZoom = useAppSelector((state) => state.timelinePreferences.value.timelineZoom)
    const pendingAnchor = useRef<PendingAnchor | null>(null)

    const getZoom = () => store.getState().timelinePreferences.value.timelineZoom

    useWheelZoom({
        ref: scrollRef,
        min: timelineMinZoom,
        max: timelineMaxZoom,
        getZoom,
        onZoom: (zoom, anchor) => {
            const element = scrollRef.current
            if (element) {
                const viewportOffset = Math.max(
                    anchor.clientX -
                        element.getBoundingClientRect().left -
                        TIMELINE_ROW.headingWidth,
                    0
                )
                pendingAnchor.current = {
                    timeOffset: element.scrollLeft + viewportOffset,
                    zoom: getZoom(),
                    viewportOffset,
                }
            }
            // the store only — persisting every frame would flood the backend. The time-step
            // density follows on its own, TimelineControlTools watches the zoom.
            dispatch(setTimelinePreferencesFulfilled({ timelineZoom: zoom }))
        },
        onZoomEnd: (zoom) => dispatch(setTimelinePreferencesAsync("timelineZoom", zoom)),
    })

    useLayoutEffect(() => {
        const anchor = pendingAnchor.current
        pendingAnchor.current = null
        if (!anchor) return
        scrollTimelineTo(
            anchoredScroll(anchor.timeOffset, anchor.zoom, timelineZoom, anchor.viewportOffset)
        )
    }, [timelineZoom])
}

export default useTimelineWheelZoom
